export type GuideRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type GuidePosition = {
  left: number;
  top: number;
};

type Viewport = {
  width: number;
  height: number;
};

const VIEWPORT_GAP = 16;

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

function clampPosition(
  position: GuidePosition,
  guideSize: Pick<GuideRect, "width" | "height">,
  viewport: Viewport,
  gap = VIEWPORT_GAP
): GuidePosition {
  return {
    left: clamp(position.left, gap, viewport.width - guideSize.width - gap),
    top: clamp(position.top, gap, viewport.height - guideSize.height - gap),
  };
}

function rectAt(
  position: GuidePosition,
  guideSize: Pick<GuideRect, "width" | "height">
): GuideRect {
  return { ...position, width: guideSize.width, height: guideSize.height };
}

function intersectionArea(first: GuideRect, second: GuideRect) {
  const width = Math.max(
    0,
    Math.min(first.left + first.width, second.left + second.width) -
      Math.max(first.left, second.left)
  );
  const height = Math.max(
    0,
    Math.min(first.top + first.height, second.top + second.height) -
      Math.max(first.top, second.top)
  );
  return width * height;
}

function distanceBetween(first: GuidePosition, second: GuidePosition) {
  return Math.abs(first.left - second.left) + Math.abs(first.top - second.top);
}

export function chooseGuidePosition({
  target,
  guideSize,
  viewport,
  manualPosition,
}: {
  target?: GuideRect | null;
  guideSize: Pick<GuideRect, "width" | "height">;
  viewport: Viewport;
  manualPosition?: GuidePosition | null;
}): GuidePosition {
  const defaultPosition = clampPosition(
    {
      left: VIEWPORT_GAP,
      top: viewport.height - guideSize.height - VIEWPORT_GAP,
    },
    guideSize,
    viewport
  );

  if (!target) {
    return manualPosition
      ? clampPosition(manualPosition, guideSize, viewport)
      : defaultPosition;
  }

  const manualCandidate = manualPosition
    ? clampPosition(manualPosition, guideSize, viewport)
    : null;
  if (
    manualCandidate &&
    intersectionArea(rectAt(manualCandidate, guideSize), target) === 0
  ) {
    return manualCandidate;
  }

  const right = viewport.width - guideSize.width - VIEWPORT_GAP;
  const bottom = viewport.height - guideSize.height - VIEWPORT_GAP;
  const targetCandidates: GuidePosition[] = [
    { left: VIEWPORT_GAP, top: bottom },
    { left: right, top: bottom },
    { left: VIEWPORT_GAP, top: VIEWPORT_GAP },
    { left: right, top: VIEWPORT_GAP },
    { left: target.left + target.width + VIEWPORT_GAP, top: target.top },
    { left: target.left - guideSize.width - VIEWPORT_GAP, top: target.top },
    { left: target.left, top: target.top + target.height + VIEWPORT_GAP },
    { left: target.left, top: target.top - guideSize.height - VIEWPORT_GAP },
  ];

  const reference = manualCandidate ?? defaultPosition;
  return (
    targetCandidates
      .map((candidate) => clampPosition(candidate, guideSize, viewport))
      .sort((first, second) => {
        const firstOverlap = intersectionArea(rectAt(first, guideSize), target);
        const secondOverlap = intersectionArea(
          rectAt(second, guideSize),
          target
        );
        if (firstOverlap !== secondOverlap) return firstOverlap - secondOverlap;
        return (
          distanceBetween(first, reference) - distanceBetween(second, reference)
        );
      })[0] ?? defaultPosition
  );
}

export function doesGuideOverlapTarget(
  position: GuidePosition,
  guideSize: Pick<GuideRect, "width" | "height">,
  target?: GuideRect | null
) {
  return target
    ? intersectionArea(rectAt(position, guideSize), target) > 0
    : false;
}
